/**
 * IANA timezone list used by the Settings → Business timezone picker.
 *
 * Modern browsers expose the full list via Intl.supportedValuesOf('timeZone')
 * — we use it when available and fall back to a curated list of the most
 * common zones (covering the Americas / Europe / Asia / Africa / Oceania).
 *
 * For each zone we also surface a friendly label like
 * "(GMT-03:00) America/Sao_Paulo" so the picker is searchable / readable.
 */

const FALLBACK_TIMEZONES: string[] = [
    // Americas
    'America/Adak', 'America/Anchorage', 'America/Argentina/Buenos_Aires',
    'America/Bogota', 'America/Caracas', 'America/Chicago', 'America/Costa_Rica',
    'America/Denver', 'America/Edmonton', 'America/El_Salvador', 'America/Fortaleza',
    'America/Guatemala', 'America/Halifax', 'America/Havana', 'America/Indianapolis',
    'America/Jamaica', 'America/La_Paz', 'America/Lima', 'America/Los_Angeles',
    'America/Manaus', 'America/Mazatlan', 'America/Mexico_City', 'America/Monterrey',
    'America/Montevideo', 'America/Nassau', 'America/New_York', 'America/Noronha',
    'America/Panama', 'America/Phoenix', 'America/Port-au-Prince', 'America/Recife',
    'America/Regina', 'America/Rio_Branco', 'America/Santiago', 'America/Santo_Domingo',
    'America/Sao_Paulo', 'America/St_Johns', 'America/Tegucigalpa', 'America/Tijuana',
    'America/Toronto', 'America/Vancouver', 'America/Winnipeg',
    // Europe
    'Europe/Amsterdam', 'Europe/Athens', 'Europe/Belgrade', 'Europe/Berlin',
    'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest', 'Europe/Copenhagen',
    'Europe/Dublin', 'Europe/Helsinki', 'Europe/Istanbul', 'Europe/Kiev',
    'Europe/Lisbon', 'Europe/London', 'Europe/Madrid', 'Europe/Moscow',
    'Europe/Oslo', 'Europe/Paris', 'Europe/Prague', 'Europe/Rome',
    'Europe/Sofia', 'Europe/Stockholm', 'Europe/Vienna', 'Europe/Warsaw',
    'Europe/Zurich',
    // Africa
    'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg', 'Africa/Lagos',
    'Africa/Nairobi',
    // Asia
    'Asia/Bangkok', 'Asia/Dubai', 'Asia/Ho_Chi_Minh', 'Asia/Hong_Kong',
    'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Kolkata', 'Asia/Kuala_Lumpur',
    'Asia/Manila', 'Asia/Riyadh', 'Asia/Seoul', 'Asia/Shanghai',
    'Asia/Singapore', 'Asia/Taipei', 'Asia/Tehran', 'Asia/Tokyo',
    // Australia / Pacific
    'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Melbourne',
    'Australia/Perth', 'Australia/Sydney', 'Pacific/Auckland', 'Pacific/Fiji',
    'Pacific/Guam', 'Pacific/Honolulu',
    // Catch-all
    'UTC',
];

/**
 * The full set of supported zones for this browser, sorted alphabetically.
 * Uses `Intl.supportedValuesOf` when available (Chrome 99+, Safari 15.4+,
 * Firefox 93+), otherwise falls back to the static list above.
 */
export const ALL_TIMEZONES: string[] = (() => {
    try {
        const fn = (Intl as unknown as {
            supportedValuesOf?: (k: 'timeZone') => string[];
        }).supportedValuesOf;
        if (typeof fn === 'function') {
            return fn('timeZone').slice().sort();
        }
    } catch {
        /* fall through */
    }
    return FALLBACK_TIMEZONES.slice().sort();
})();

/**
 * Compute the GMT offset label for a zone — e.g. "GMT-03:00" for
 * America/Sao_Paulo. Cached so the picker doesn't recompute on every render.
 */
const OFFSET_CACHE: Record<string, string> = {};

export const timezoneOffsetLabel = (tz: string, when: Date = new Date()): string => {
    if (OFFSET_CACHE[tz]) return OFFSET_CACHE[tz];
    try {
        const dtf = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'shortOffset',
            hour: '2-digit',
        });
        const part = dtf.formatToParts(when).find((p) => p.type === 'timeZoneName');
        // Normalize "GMT" / "GMT-3" / "GMT-03:00" → "GMT-03:00"
        const raw = (part?.value ?? 'GMT').replace(/^UTC/, 'GMT');
        if (raw === 'GMT') return (OFFSET_CACHE[tz] = 'GMT+00:00');
        const match = raw.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
        if (!match) return (OFFSET_CACHE[tz] = raw);
        const sign = match[1];
        const hh = match[2].padStart(2, '0');
        const mm = match[3] ?? '00';
        return (OFFSET_CACHE[tz] = `GMT${sign}${hh}:${mm}`);
    } catch {
        return 'GMT';
    }
};

/**
 * Migration helper: the project used legacy abbreviations (BRT, AMT, ACT, FNT)
 * before switching to IANA names. Returns the IANA zone for legacy values so
 * existing businesses don't break.
 */
export const normalizeTimezone = (
    value: string | null | undefined
): string => {
    if (!value) return 'UTC';
    let v = value.trim();
    if (ALL_TIMEZONES.includes(v)) return v;
    // Recover a malformed value that a pre-validation save may have stored:
    //  - a display label "(GMT-03:00) America/Sao Paulo" → strip the offset prefix;
    //  - a spaced region/city "America/Sao Paulo" → underscore it.
    // Without this, ZoneId.of on the backend rejects the stale value and the
    // whole Details save fails with "Fuso horário inválido".
    const noOffset = v.replace(/^\((?:GMT|UTC)[^)]*\)\s*/i, '').trim();
    const underscored = noOffset.replace(/\s+/g, '_');
    if (ALL_TIMEZONES.includes(underscored)) return underscored;
    v = noOffset;
    switch (v.toUpperCase()) {
        case 'BRT': return 'America/Sao_Paulo';
        case 'AMT': return 'America/Manaus';
        case 'ACT': return 'America/Rio_Branco';
        case 'FNT': return 'America/Noronha';
        case 'EST': return 'America/New_York';
        case 'CST': return 'America/Chicago';
        case 'MST': return 'America/Denver';
        case 'PST': return 'America/Los_Angeles';
        case 'GMT': return 'UTC';
        case 'UTC': return 'UTC';
        default:    return underscored; // unknown — let the select show it as a raw option
    }
};
